/* eslint-disable no-restricted-imports -- File defines overrides */
import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useTrackLoader } from 'platform/components/loader/LoaderContext';
import { SystemError } from 'platform/Error';
import { useStalePageCheck } from 'platform/hooks/useReloadStalePage';
import { useObservability } from 'platform/observability';
import { useToPath } from 'platform/preserveQueryParams';
import { useNavigate as useRouterNavigate } from 'react-router';
import { normalizePathname, usePath } from 'shared/hooks/usePath';
import { trackNavigationEvent } from 'shared/hooks/useTrackPerformance';

import type { NavigateOptions, NavigateProps, To } from './types';

/* eslint-enable no-restricted-imports  -- File defines overrides */

export type { To } from './types';

/**
 * Synchronous-return signature used by cake's navigate wrapper — RR7's NavigateFunction
 * returns Promise<void>, but our wrapper schedules navigation in a background async IIFE
 * and returns void synchronously, so callers can pass it to `() => void` props directly.
 */
type SyncNavigate = (...args: NavigateParams) => void;
type NavigateParams = [to: To, options?: NavigateOptions] | [delta: number];

/**
 * Component that tracks all the redirects and ensures that url params are passed to the new route
 *
 * @override
 */
export const Navigate: FC<
  NavigateProps & {
    isFallback?: boolean;
    customDisallowedSearchParams?: string[];
  }
> = ({ to, isFallback, customDisallowedSearchParams, ...props }) => {
  const { trackEvent, trackError } = useObservability();
  const from = usePath();
  const checkStalePageAndAuth = useStalePageCheck();
  const navigate = useRouterNavigate();

  if (!to) {
    throw new SystemError('Navigate component must have a properly formatted `to` prop');
  }

  const getToPath = useToPath(customDisallowedSearchParams);

  useEffect(() => {
    let isMounted = true;

    const doNavigate = async () => {
      try {
        const hasNavigated = await checkStalePageAndAuth();
        if (hasNavigated) {
          return;
        }
      } catch (e) {
        // eslint-disable-next-line @opendoor/track-error-prefer-throw -- Navigation still proceeds if the stale/auth preflight fails.
        trackError('failed to check staleness/auth', e as Error);
      }

      if (!isMounted) {
        return;
      }

      const toPath = getToPath(to);

      trackEvent(
        props.replace ? 'redirect' : 'navigate',
        normalizePathname(toPath.pathname ?? ''),
        {
          from,
          fallback: isFallback,
          ...props?.state,
        },
      );
      trackNavigationEvent();

      await navigate(toPath, props);
    };

    void doNavigate();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on load
  }, []);

  useTrackLoader('platform:navigate', true);
  return null;
};

/**
 * Hook that tracks all the redirects and ensures that url params are passed to the new route
 * @override
 */
export const useNavigate = (customDisallowedSearchParams?: string[]): SyncNavigate => {
  const navigate = useRouterNavigate();
  const { trackEvent, trackError } = useObservability();
  const from = usePath();
  const getToPath = useToPath(customDisallowedSearchParams);
  const checkStalePageAndAuth = useStalePageCheck();
  const callIdRef = useRef(0);

  return useCallback<SyncNavigate>(
    (...props: NavigateParams) => {
      const callId = ++callIdRef.current;
      void (async () => {
        try {
          const hasNavigated = await checkStalePageAndAuth();
          if (hasNavigated) {
            return;
          }
        } catch (e) {
          // eslint-disable-next-line @opendoor/track-error-prefer-throw -- Navigation still proceeds if the stale/auth preflight fails.
          trackError('failed to check staleness/auth', e as Error);
        }

        // A newer navigation call arrived while we were awaiting — abort this one.
        if (callId !== callIdRef.current) {
          return;
        }

        trackNavigationEvent();
        const [deltaOrTo, options] = props;

        // Numerically navigate the history stack
        if (typeof deltaOrTo === 'number') {
          trackEvent('navigate', `Navigate the history stack by ${deltaOrTo}`, { from });
          await navigate(deltaOrTo);
          return;
        }

        // Navigate to specific new location while maintaining search params
        const toPath = getToPath(deltaOrTo);
        trackEvent(
          options?.replace ? 'redirect' : 'navigate',
          normalizePathname(toPath.pathname ?? ''),
          { from, ...options?.state },
        );
        await navigate(toPath, options);
      })();
    },
    [checkStalePageAndAuth, from, getToPath, navigate, trackError, trackEvent],
  );
};

/**
 * Fallback component that tracks all the redirects and ensures that url params are passed to the new route. This is a simple extension of the Navigate component defined above
 * @override
 */
export const Fallback: FC<NavigateProps & { customDisallowedSearchParams?: string[] }> = (
  props,
) => <Navigate {...props} isFallback />;
